<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon>
          </v-btn>
          <span>{{ isEditMode ? 'Edit' : 'View' }} Bank Detail</span>
          <v-spacer></v-spacer>
          <v-btn
            v-if="!isEditMode"
            color="primary"
            @click="enableEditMode"
            :disabled="isLoading"
          >
            <v-icon left>mdi-pencil</v-icon>
            Edit Data
          </v-btn>
          <div v-if="isEditMode" class="d-flex gap-2">
            <v-btn
              color="success"
                @click="save"
              :loading="isLoading"
              :disabled="isLoading"
            >
              <v-icon left>mdi-content-save</v-icon>
              Save Changes
            </v-btn>
            <v-btn
              color="grey"
                @click="cancelEdit"
              :disabled="isLoading"
            >
              <v-icon left>mdi-close</v-icon>
              Cancel
            </v-btn>
          </div>
        </v-card-title>
        <v-card-text>
          <!-- Read-Only View -->
          <div v-if="!isEditMode">
            <v-card elevation="2" rounded="lg" class="mb-4">
              <v-card-title class="primary white--text">
                <v-icon left color="white">mdi-bank</v-icon>
                Bank Detail Information
              </v-card-title>
              <v-card-text class="pt-4">
                <v-row>
                  <!-- Primary Bank Information -->
                  <v-col cols="12">
                    <h4 class="mb-3 text-primary">Primary Bank Information</h4>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      :value="form.name || 'N/A'"
                      outlined
                      label="Account Holder Name"
                      prepend-inner-icon="mdi-account"
                      readonly
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      :value="form.account_number || 'N/A'"
                      outlined
                      label="Account Number"
                      prepend-inner-icon="mdi-credit-card"
                      readonly
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      :value="form.bank_name || 'N/A'"
                      outlined
                      label="Bank Name"
                      prepend-inner-icon="mdi-bank"
                      readonly
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      :value="form.branch || 'N/A'"
                      outlined
                      label="Branch Name"
                      prepend-inner-icon="mdi-office-building"
                      readonly
                    ></v-text-field>
                  </v-col>
                  
                  <!-- Bank Codes & Location -->
                  <v-col cols="12">
                    <h4 class="mb-3 mt-4 text-primary">Bank Codes & Location</h4>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      :value="form.ifsc || 'N/A'"
                      outlined
                      label="IFSC Code"
                      prepend-inner-icon="mdi-code-string"
                      readonly
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      :value="form.micr || 'N/A'"
                      outlined
                      label="MICR Code"
                      prepend-inner-icon="mdi-numeric"
                      readonly
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      :value="form.city || 'N/A'"
                      outlined
                      label="City"
                      prepend-inner-icon="mdi-map-marker"
                      readonly
                    ></v-text-field>
                  </v-col>
                  
                  <!-- Account Configuration -->
                  <v-col cols="12">
                    <h4 class="mb-3 mt-4 text-primary">Account Configuration</h4>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      :value="getUserName(form.user_id)"
                      outlined
                      label="Associated User"
                      prepend-inner-icon="mdi-account-outline"
                      readonly
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      :value="form.is_company ? 'Company Account' : 'Personal Account'"
                      outlined
                      label="Account Type"
                      prepend-inner-icon="mdi-account-box"
                      readonly
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      :value="form.status ? 'Active' : 'Inactive'"
                      outlined
                      label="Account Status"
                      prepend-inner-icon="mdi-check-circle"
                      readonly
                      :color="form.status ? 'success' : 'error'"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      :value="form.is_active ? 'Active' : 'Inactive'"
                      outlined
                      label="Is Active"
                      prepend-inner-icon="mdi-power"
                      readonly
                      :color="form.is_active ? 'success' : 'error'"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      :value="formatDate(form.created_at)"
                      outlined
                      label="Created Date"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </div>

          <!-- Edit Mode -->
          <div v-if="isEditMode">
            <v-row>
              <!-- Primary Bank Information -->
              <v-col cols="12">
                <h4 class="mb-3 text-primary">Primary Bank Information</h4>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.name"
                  outlined
                  label="Account Holder Name *"
                  prepend-inner-icon="mdi-account"
                  :error-messages="errors.name"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.account_number"
                  outlined
                  label="Account Number *"
                  prepend-inner-icon="mdi-credit-card"
                  :error-messages="errors.account_number"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.bank_name"
                  outlined
                  label="Bank Name"
                  prepend-inner-icon="mdi-bank"
                  :error-messages="errors.bank_name"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.branch"
                  outlined
                  label="Branch Name"
                  prepend-inner-icon="mdi-office-building"
                  :error-messages="errors.branch"
                ></v-text-field>
              </v-col>
              
              <!-- Bank Codes & Location -->
              <v-col cols="12">
                <h4 class="mb-3 mt-4 text-primary">Bank Codes & Location</h4>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="form.ifsc"
                  outlined
                  label="IFSC Code"
                  prepend-inner-icon="mdi-code-string"
                  :error-messages="errors.ifsc"
                  placeholder="e.g., SBIN0001234"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="form.micr"
                  outlined
                  label="MICR Code"
                  prepend-inner-icon="mdi-numeric"
                  :error-messages="errors.micr"
                  placeholder="e.g., 123456789"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="form.city"
                  outlined
                  label="City"
                  prepend-inner-icon="mdi-map-marker"
                  :error-messages="errors.city"
                ></v-text-field>
              </v-col>
              
              <!-- Account Configuration -->
              <v-col cols="12">
                <h4 class="mb-3 mt-4 text-primary">Account Configuration</h4>
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  ref="user"
                  outlined
                  v-model="form.user_id"
                  :items="userItems"
                  :loading="isLoadingUsers"
                  :search-input.sync="userSearch"
                  label="Search Associated User (Optional)"
                  prepend-inner-icon="mdi-account-outline"
                  no-data-text="Type to search users"
                  item-text="text"
                  item-value="value"
                  clearable
                  :error-messages="errors.user_id"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" md="6">
                <div class="mt-2">
                  <v-switch
                    v-model="form.is_company"
                    inset
                    :label="`Account Type: ${form.is_company ? 'Company' : 'Personal'}`"
                    color="primary"
                  ></v-switch>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="mt-2">
                  <v-switch
                    v-model="form.status"
                    inset
                    :label="`Account Status: ${form.status ? 'Active' : 'Inactive'}`"
                    color="success"
                  ></v-switch>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="mt-2">
                  <v-switch
                    v-model="form.is_active"
                    inset
                    :label="`Is Active: ${form.is_active ? 'YES' : 'NO'}`"
                    color="primary"
                  ></v-switch>
                </div>
              </v-col>
            </v-row>
          </div>
          <!-- Action Buttons - Only show delete in read-only mode -->
          <v-row v-if="!isEditMode">
            <v-col sm="12" class="text-center">
              <v-btn
                color="error"
                @click="confirmDelete"
                :disabled="isLoading"
              >
                <v-icon left>mdi-delete</v-icon>
                Delete Bank Detail
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-container>
    <!-- Success/Error Dialog -->
    <v-dialog v-model="dialogVisible" width="600">
      <v-card rounded="xxl">
        <v-container>
          <v-row align="center">
            <v-col sm="3" align="center">
              <v-icon
                large
                :style="dialogSuccess ? 'color: green; font-size: 100px; width: 200px !important' : 'color: red; font-size: 100px; width: 200px !important'"
              >
                {{ dialogSuccess ? 'mdi-check-circle-outline' : 'mdi-alert-remove-outline' }}
              </v-icon>
            </v-col>
            <v-col sm="9" align="center">
              <v-card-text>
                <v-row>
                  <v-col>
                    <h1 :style="dialogSuccess ? 'color: green' : 'color: red'">
                      {{ dialogSuccess ? 'SUCCESS!' : 'OOPS!' }}
                    </h1>
                    <br />
                    <h3>{{ dialogMessage }}</h3>
                    <br />
                    <v-btn :color="dialogSuccess ? 'green' : 'red'" @click="handleDialogClose">
                      OK
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>
    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" width="500">
      <v-card rounded="xxl">
        <v-card-title class="headline">
          <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
          Confirm Delete
        </v-card-title>
        <v-card-text>
          Are you sure you want to delete this bank detail? This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="deleteDialog = false">
            Cancel
          </v-btn>
          <v-btn color="error" @click="deleteBankDetail" :loading="deleteLoading">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import axios from "axios";
export default {
  name: "BankDetailEditPage",
  data() {
    return {
      form: {
        user_id: null,
        is_company: false,
        name: "",
        account_number: "",
        bank_name: "",
        branch: "",
        ifsc: "",
        micr: "",
        city: "",
        status: false,
        is_active: true,
      },
      errors: {},
      isLoading: false,
      deleteLoading: false,
      isEditMode: false,
      dialogVisible: false,
      deleteDialog: false,
      dialogMessage: "",
      dialogSuccess: false,
      accountTypeItems: [
        { id: 1, text: "Savings Account", value: 1 },
        { id: 2, text: "Current Account", value: 2 },
        { id: 3, text: "Fixed Deposit", value: 3 },
        { id: 4, text: "Recurring Deposit", value: 4 },
        { id: 5, text: "NRI Account", value: 5 },
      ],
      userItems: [],
      originalData: {},
      isLoadingUsers: false,
      userSearch: null,
      searchTimeout: null,
    };
  },
  watch: {
    userSearch(val) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      
      this.searchTimeout = setTimeout(() => {
        this.searchUsers(val);
      }, 300);
    },
  },
  mounted() {
    this.getData();
    // Don't load all users on mount, but load current user for display
  },
  methods: {
    async searchUsers(searchTerm) {
      if (!searchTerm || searchTerm.length < 2) {
        // Keep current user if no search term, otherwise clear
        if (this.form.user_id && this.originalData.user) {
          const user = this.originalData.user;
          this.userItems = [{
            id: user.id,
            text: `${user.first_name}${user.last_name ? ' ' + user.last_name : ''} (${user.phone})`,
            value: user.id,
          }];
        } else {
          this.userItems = [];
        }
        return;
      }

      try {
        this.isLoadingUsers = true;
        const response = await axios.get(`users?search=${searchTerm}&limit=50`);
        
        if (response.data.success && response.data.data) {
          const searchResults = response.data.data.map((user) => ({
            id: user.id,
            text: `${user.first_name}${user.last_name ? ' ' + user.last_name : ''} (${user.phone})`,
            value: user.id,
          }));
          
          // Always include the current user in the results if it exists
          if (this.form.user_id && this.originalData.user) {
            const currentUser = {
              id: this.originalData.user.id,
              text: `${this.originalData.user.first_name}${this.originalData.user.last_name ? ' ' + this.originalData.user.last_name : ''} (${this.originalData.user.phone})`,
              value: this.originalData.user.id,
            };
            
            // Check if current user is already in search results
            const currentUserExists = searchResults.some(u => u.value === currentUser.value);
            if (!currentUserExists) {
              this.userItems = [currentUser, ...searchResults];
            } else {
              this.userItems = searchResults;
            }
          } else {
            this.userItems = searchResults;
          }
        }
      } catch (error) {
        console.log("Error searching users:", error);
        // Fallback to current user if search fails
        if (this.form.user_id && this.originalData.user) {
          const user = this.originalData.user;
          this.userItems = [{
            id: user.id,
            text: `${user.first_name}${user.last_name ? ' ' + user.last_name : ''} (${user.phone})`,
            value: user.id,
          }];
        } else {
          this.userItems = [];
        }
      } finally {
        this.isLoadingUsers = false;
      }
    },

    async getUsers() {
      // Legacy method - now only loads initial data if needed
      try {
        const response = await axios.get("users?limit=20");
        if (response.data.success && response.data.data) {
          this.userItems = response.data.data.map(user => ({
            id: user.id,
            text: `${user.first_name}${user.last_name ? ' ' + user.last_name : ''} (${user.phone})`,
            value: user.id,
          }));
        }
      } catch (error) {
        console.log("Error loading users:", error);
      }
    },
    async getData() {
      this.isLoading = true;
      let encryptedId = this.$route.params.id;
      
      try {
        const response = await axios.get(`/bank_details/${encryptedId}`);
        if (response.data.success) {
          this.form = { ...this.form, ...response.data.data };
          this.originalData = { ...response.data.data };
          
          // Load the current user info for display purposes
          if (response.data.data.user_id && response.data.data.user) {
            const user = response.data.data.user;
            this.userItems = [{
              id: user.id,
              text: `${user.first_name}${user.last_name ? ' ' + user.last_name : ''} (${user.phone})`,
              value: user.id,
            }];
          }
        }
      } catch (error) {
        console.error("Error fetching bank detail:", error);
        this.dialogMessage = "Failed to load bank detail data";
        this.dialogSuccess = false;
        this.dialogVisible = true;
      }
      
      this.isLoading = false;
    },
    async save() {
      try {
        this.isLoading = true;
        this.errors = {};

        const response = await axios.patch(`/bank_details/${this.$route.params.id}`, this.form);

        if (response.data.success) {
          this.dialogMessage = "Bank detail updated successfully!";
          this.dialogSuccess = true;
          this.dialogVisible = true;
        } else {
          const errorMessage = response.data.sub_title || response.data.message || "Update failed";
          this.dialogMessage = errorMessage;
          this.dialogSuccess = false;
          this.dialogVisible = true;
        }
      } catch (e) {
        console.error("Error updating bank detail:", e);
        
        if (e.response && e.response.status === 422) {
          this.errors = e.response.data.errors || {};
          const firstError = Object.values(this.errors)[0];
          this.dialogMessage = firstError ? firstError[0] : "Please check your input data";
        } else {
          const errorMessage = e.response?.data?.sub_title || 
                              e.response?.data?.message || 
                              "An error occurred while updating the bank detail";
          this.dialogMessage = errorMessage;
        }
        
        this.dialogSuccess = false;
        this.dialogVisible = true;
      } finally {
        this.isLoading = false;
      }
    },
    confirmDelete() {
      this.deleteDialog = true;
    },
    async deleteBankDetail() {
      try {
        this.deleteLoading = true;

        const response = await axios.post(`/bank_details/delete/${this.$route.params.id}`);

        if (response.data.success) {
          this.deleteDialog = false;
          this.dialogMessage = "Bank detail deleted successfully!";
          this.dialogSuccess = true;
          this.dialogVisible = true;
        } else {
          this.deleteDialog = false;
          const errorMessage = response.data.sub_title || response.data.message || "Delete failed";
          this.dialogMessage = errorMessage;
          this.dialogSuccess = false;
          this.dialogVisible = true;
        }
      } catch (error) {
        console.error("Error deleting bank detail:", error);
        this.deleteDialog = false;
        const errorMessage = error.response?.data?.sub_title || 
                            error.response?.data?.message || 
                            "An error occurred while deleting the bank detail";
        this.dialogMessage = errorMessage;
        this.dialogSuccess = false;
        this.dialogVisible = true;
      } finally {
        this.deleteLoading = false;
      }
    },
    enableEditMode() {
      this.isEditMode = true;
      this.errors = {};
      
      // Ensure current user is available in dropdown when entering edit mode
      if (this.form.user_id && this.originalData.user) {
        const user = this.originalData.user;
        this.userItems = [{
          id: user.id,
          text: `${user.first_name}${user.last_name ? ' ' + user.last_name : ''} (${user.phone})`,
          value: user.id,
        }];
      }
    },
    cancelEdit() {
      this.form = { ...this.originalData };
      this.isEditMode = false;
      this.errors = {};
      
      // Restore current user in dropdown when canceling edit
      if (this.form.user_id && this.originalData.user) {
        const user = this.originalData.user;
        this.userItems = [{
          id: user.id,
          text: `${user.first_name}${user.last_name ? ' ' + user.last_name : ''} (${user.phone})`,
          value: user.id,
        }];
      }
    },
    getUserName(userId) {
      if (!userId) return 'N/A';
      const user = this.userItems.find(u => u.value === userId);
      return user ? user.text : 'Unknown User';
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    handleDialogClose() {
      this.dialogVisible = false;
      if (this.dialogSuccess) {
        if (this.isEditMode) {
          this.isEditMode = false;
          this.getData(); // Refresh data
        } else {
          this.$router.push("/bank_details");
        }
      }
    },
  },
};
</script>